import {
  ChartMark,
  WidgetKind,
  type Filters,
  type ListOf,
  type Report,
} from 'corso-admin-ui';

const inspected = { hasInspection: true } satisfies Filters<'claimItems'>;
// pinned to the day of delivery: an item with no inspection has no inspection date to be found by
const awaitingInspection = {
  filters: { hasInspection: false },
  timeField: 'deliveredOn',
} as const;

const byDelivery = 'By the day the item was delivered';
const byInspection = 'By the day the item was inspected';

type Column = ListOf<'claimItems'>['columns'][number];
const created = { column: 'createdOn', title: 'Created' } satisfies Column;
const delivered = {
  column: 'deliveredOn',
  title: 'Delivered',
} satisfies Column;
const itemColumns = [
  { column: 'productTitle', title: 'Product' },
  { column: 'sku', title: 'SKU' },
  { column: 'orderNumber', title: 'Order' },
  { column: 'quantity', title: 'Quantity' },
  { column: 'resolution', title: 'Resolution' },
] as const satisfies Column[];

export const inspections = {
  key: 'inspections',
  label: 'Inspections',
  dataset: 'claimItems',
  timeFields: ['inspectionCreatedOn'],
  widgets: [
    {
      kind: WidgetKind.kpi,
      title: 'Line items delivered',
      description: byDelivery,
      metric: 'lineItems',
      timeField: 'deliveredOn',
    },
    {
      kind: WidgetKind.kpi,
      title: 'Line items inspected',
      description: byInspection,
      metric: 'lineItems',
      filters: inspected,
    },
    {
      kind: WidgetKind.overTime,
      title: 'Delivered without an inspection',
      description: byDelivery,
      mark: ChartMark.bar,
      metrics: ['lineItems'],
      ...awaitingInspection,
    },
    {
      kind: WidgetKind.overTime,
      title: 'Inspections over time',
      description: byInspection,
      mark: ChartMark.bar,
      metrics: ['lineItems'],
      filters: inspected,
    },
    {
      kind: WidgetKind.ranking,
      title: 'Delivered without an inspection by resolution',
      description: byDelivery,
      dimension: 'resolution',
      metric: 'lineItems',
      ...awaitingInspection,
    },
    {
      kind: WidgetKind.ranking,
      title: 'Inspection grades',
      description: byInspection,
      dimension: 'inspectionGrade',
      metric: 'lineItems',
      filters: inspected,
    },
    {
      kind: WidgetKind.table,
      title: 'Inspection grades to resolutions',
      description: 'Open a grade to see how its items were resolved',
      dimensions: ['inspectionGrade', 'resolution'],
      metrics: ['lineItems'],
      filters: inspected,
    },
    {
      kind: WidgetKind.table,
      title: 'Inspection grades by SKU',
      description: byInspection,
      dimensions: ['sku', 'productTitle', 'inspectionGrade'],
      metrics: ['items'],
      limit: 50,
      filters: inspected,
    },
    {
      kind: WidgetKind.list,
      title: 'Inspected items',
      description: byInspection,
      list: 'claimItems',
      timeFields: ['inspectionCreatedOn'],
      columns: [
        created,
        delivered,
        { column: 'inspectionCreatedOn', title: 'Inspected' },
        { column: 'inspectionGrade', title: 'Grade' },
        ...itemColumns,
      ],
      filters: inspected,
    },
    {
      kind: WidgetKind.list,
      title: 'Delivered items without an inspection',
      description: byDelivery,
      list: 'claimItems',
      timeFields: [awaitingInspection.timeField],
      columns: [created, delivered, ...itemColumns],
      filters: awaitingInspection.filters,
    },
  ],
} as const satisfies Report;
